import {
  convertKrutiDev010ToUnicode,
} from './krutiDev010';

/**
 * Convert Kruti Dev 714 legacy text to Unicode Devanagari.
 *
 * Kruti Dev 714 belongs to the Kruti Dev / Remington legacy family.
 *
 * IMPORTANT:
 * At present we use the verified standard Kruti Dev conversion core.
 * Do NOT add guessed 714-specific substitutions unless validated against
 * known Kruti Dev 714 source -> Unicode sample pairs.
 *
 * Keeping this as a separate function is intentional. If a 714-specific
 * mapping difference is discovered later, it can be added here without
 * affecting Kruti Dev 010/011 or 055.
 */

const normalizeLegacyInput = (text = '') =>
  text
    .replace(/\u00A0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

/**
 * Characters left after conversion that are often a sign that a legacy
 * glyph was not understood.
 *
 * Do not automatically modify them. Flag them for review instead.
 */
const findSuspiciousLegacyCharacters = (text = '') => {
  const suspicious = text.match(
    /[¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞß]/g
  );

  return [...new Set(suspicious || [])];
};


/**
 * Basic Kruti Dev-looking ASCII heuristic.
 *
 * This is intentionally NOT used to decide whether conversion is correct.
 * It is only useful for diagnostics.
 */
const hasPossibleLegacyText = (text = '') =>
  /[A-Za-z;:[\]{}<>@\\]/.test(text);


/**
 * Main Kruti Dev 714 conversion.
 *
 * @param {string} text
 * @returns {string}
 */
export const convertKrutiDev714ToUnicode = (text = '') => {
  if (!text) return '';

  const normalized = normalizeLegacyInput(text);

  /*
   * 714 currently uses the verified common Kruti Dev conversion engine.
   *
   * Unlike Kruti Dev 055, we do not apply any additional replacement
   * rules here until a 714-specific difference has been demonstrated
   * with verified source/Unicode pairs.
   */
  const converted =
    convertKrutiDev010ToUnicode(normalized);

  return String(converted || '').normalize('NFC');
};


/**
 * Diagnostic version.
 *
 * Useful for legal-document ingestion because it keeps conversion warnings
 * separate from the converted text.
 */
export const convertKrutiDev714WithDiagnostics = (text = '') => {
  const source = text || '';

  if (!source) {
    return {
      text: '',
      sourceFont: 'Kruti Dev 714',
      warnings: [],
      suspiciousCharacters: [],
    };
  }

  const converted =
    convertKrutiDev714ToUnicode(source);

  const suspiciousCharacters =
    findSuspiciousLegacyCharacters(converted);

  const warnings = [];

  if (suspiciousCharacters.length > 0) {
    warnings.push(
      'Some legacy characters remain after Kruti Dev 714 conversion.'
    );
  }

  /*
   * If large portions still look like Kruti Dev ASCII after conversion,
   * conversion may be incomplete.
   *
   * English words/numbers may legitimately remain, so this is only a
   * warning rather than an error.
   */
  if (hasPossibleLegacyText(converted)) {
    warnings.push(
      'ASCII characters remain after conversion. Review mixed English/legacy text if necessary.'
    );
  }

  return {
    text: converted,
    sourceFont: 'Kruti Dev 714',
    warnings,
    suspiciousCharacters,
  };
};